import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Briefcase,
  Scale,
  ShieldCheck,
  FileSignature,
  Users,
  Landmark,
  ArrowRight,
} from "lucide-react";

export const Route = createFileRoute("/case-studies")({
  head: () => ({
    meta: [
      { title: "Case Studies — Anonymised Examples | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "Anonymised case studies showing how Nkadimeng Danny Legal & Compliance Support prepares labour, CCMA, POPIA, contract and consumer matters across South Africa.",
      },
      { property: "og:title", content: "Case Studies — Nkadimeng Danny Legal" },
      {
        property: "og:description",
        content:
          "Anonymised examples of our paralegal and compliance work — no client names, no confidential facts.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/case-studies" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/case-studies" }],
  }),
  component: CaseStudiesPage,
});

const studies = [
  {
    icon: Briefcase,
    tag: "Labour",
    title: "Disciplinary hearing preparation",
    problem:
      "An employee was issued a charge sheet for alleged misconduct and had three working days to prepare a defence.",
    role:
      "Reviewed the charge sheet for procedural compliance under Schedule 8 of the Labour Relations Act, drafted a written defence outline, indexed supporting documents, prepared cross-examination notes and coached the employee on hearing procedure.",
    outcome:
      "Client attended the hearing with a complete, organised bundle and a clear set of responses to each charge — confident and prepared rather than reactive.",
  },
  {
    icon: Scale,
    tag: "CCMA",
    title: "CCMA conciliation & arbitration support",
    problem:
      "A dismissed employee needed to refer an unfair dismissal dispute to the CCMA within the 30-day window.",
    role:
      "Completed and lodged the LRA Form 7.11 referral, prepared a witness statement and evidence bundle, briefed the client on conciliation outcomes and what to expect at arbitration if the matter did not settle.",
    outcome:
      "Referral lodged on time and within jurisdiction. Client understood every step of the process and walked into conciliation with a structured position rather than guessing.",
  },
  {
    icon: ShieldCheck,
    tag: "POPIA",
    title: "POPIA compliance pack for a 12-person SME",
    problem:
      "A small business processing customer data had no privacy policy, no consent mechanism and no record of processing — exposing the business to regulatory and reputational risk.",
    role:
      "Drafted a plain-language privacy policy, consent clauses for sign-up flows, an operator agreement for the third-party CRM and a basic processing register tailored to the business's activities.",
    outcome:
      "The business went from zero compliance documentation to a defensible POPIA baseline in under two weeks, with a clear owner internally and a review date in the diary.",
  },
  {
    icon: FileSignature,
    tag: "Contracts",
    title: "Service agreement & letter of demand",
    problem:
      "A freelance service provider had completed work for a client who refused to pay, with no written agreement in place beyond a WhatsApp exchange.",
    role:
      "Reconstructed the agreement from the WhatsApp record, drafted a formal letter of demand under common-law principles, and produced a forward-looking service agreement template for the client to use with future customers.",
    outcome:
      "Letter of demand was sent and payment was negotiated. The new template prevents the same problem recurring.",
  },
  {
    icon: Users,
    tag: "Consumer",
    title: "Consumer Protection Act complaint preparation",
    problem:
      "A consumer wanted to escalate a defective-goods complaint to the National Consumer Commission after the supplier refused to refund or replace.",
    role:
      "Drafted the formal complaint, compiled supporting evidence, cited the relevant sections of the Consumer Protection Act and prepared the consumer for the dispute-resolution process.",
    outcome:
      "Complaint was lodged in proper form. The supplier engaged once the matter became formal, and the client reached a settlement.",
  },
  {
    icon: Landmark,
    tag: "Court Online",
    title: "Court Online filing support for an attorney",
    problem:
      "An admitted attorney with a busy practice needed reliable paralegal support to prepare and upload bundles on Court Online and CaseLines.",
    role:
      "Indexed pleadings, prepared bundles in the prescribed format, uploaded to Court Online / CaseLines and confirmed service — under the attorney's supervision and on the attorney's letterhead.",
    outcome:
      "Files were ready for hearing without the attorney having to leave their drafting desk. Ongoing collaboration continues on a brief-by-brief basis.",
  },
] as const;

function CaseStudiesPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-24">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Case studies
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            How we work — in practice
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Anonymised examples of the matters we prepare every week. No
            client names, no confidential facts — only the shape of the work
            we do and the outcomes our preparation makes possible.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="grid gap-6 md:grid-cols-2">
          {studies.map((s) => (
            <article
              key={s.title}
              className="rounded-2xl border border-border bg-card p-6 shadow-soft md:p-7"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold">
                  {s.tag}
                </span>
              </div>
              <h2 className="mt-4 font-display text-xl text-navy">{s.title}</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    Problem
                  </dt>
                  <dd className="mt-1 text-foreground/85 leading-relaxed">
                    {s.problem}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    Our role
                  </dt>
                  <dd className="mt-1 text-foreground/85 leading-relaxed">
                    {s.role}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    Outcome
                  </dt>
                  <dd className="mt-1 text-foreground/85 leading-relaxed">
                    {s.outcome}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section className="container-narrow pb-24">
        <div className="rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] text-gold">
            Scope note
          </p>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            These examples illustrate typical paralegal and compliance
            preparation. They are not legal advice and do not create an
            attorney-client relationship. Where a matter requires
            representation, we refer to an admitted attorney.
          </p>
          <Link
            to="/book"
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
          >
            Book a consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
